import { Head, Link } from "@inertiajs/react"
import {
    Activity,
    ArrowLeft, Building,
    Calendar,
    CreditCard,
    DollarSign,
    Edit,
    Mail,
    MapPin,
    Phone,
    TrendingUp,
    Users
} from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { Card} from "@/components/ui/card.tsx"
import type { UserType } from "./types.ts"

interface CommunityType {
    id: string
    code: string
    name: string
}

interface ShowProps {
    user: UserType
    communities: CommunityType[]
    can_assign_communities: boolean
    last_30_days: {
        total_collected: number | string
        collect_sessions_count: number
        assigned_tax_payers_count: number
        visited_tax_payers_count: number
    }
    assigned_devices: Array<{
        id: string
        name: string | null
        serial_number: string
        imei: string | null
        status: string
        last_sync_at: string | null
        active: boolean
        community_name: string | null
    }>
    latest_collections: Array<{
        id: string
        collected_at: string | null
        payment_method: string | null
        amount: number
        tax_payer_name: string | null
        collect_session_code: string | null
        device_name: string | null
    }>
}

export default function Show({
    user,
    can_assign_communities,
    last_30_days,
    assigned_devices,
    latest_collections
}: ShowProps) {
    const primaryDevice = assigned_devices[0]
    return (
        <>
            <Head title={`User ${user.first_name} ${user.last_name}`} />
            <div className="p-8">
                <div className="mb-6">
                    <Link href="/admin/users">
                        <Button variant="ghost" size="sm" className="mb-4 gap-2">
                            <ArrowLeft className="h-4 w-4" />
                            Retour aux utilisateurs
                        </Button>
                    </Link>

                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-3xl font-bold text-foreground">{user.first_name} {user.last_name}</h2>
                                <span
                                    className={`rounded-full px-3 py-1 text-sm font-medium ${
                                        user.status === "active" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                                    }`}
                                >
                {user.status}
              </span>
                            </div>
                            <p className="mt-1 font-mono text-sm text-muted-foreground">Code: {user.code}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Link href={`/admin/agents/${user.id}/pos`}>
                                <Button variant="outline" className="gap-2 bg-transparent">
                                    <CreditCard className="h-4 w-4" />
                                    Gestion POS
                                </Button>
                            </Link>
                            {can_assign_communities && (
                                <Link href={`/admin/users/${user.id}/communities`}>
                                    <Button variant="secondary">
                                        Assigner des communautés
                                    </Button>
                                </Link>
                            )}
                            <Link href={`/admin/users/${user.id}/edit`}>
                                <Button className="gap-2">
                                    <Edit className="h-4 w-4" />
                                    Modifier l'agent
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="mb-6 grid gap-4 md:grid-cols-4">
                    <Card className="border-border bg-card p-4">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-success/10 p-2">
                                <DollarSign className="h-5 w-5 text-success" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Collecte (30js)</p>
                                <p className="text-xl font-bold text-foreground">
                                    {Number(last_30_days?.total_collected || 0).toFixed(2)} FCFA
                                </p>
                            </div>
                        </div>
                    </Card>

                    <Card className="border-border bg-card p-4">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-primary/10 p-2">
                                <Activity className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Sessions</p>
                                <p className="text-xl font-bold text-foreground">
                                    {last_30_days?.collect_sessions_count ?? 0}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <Card className="border-border bg-card p-4">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-accent/10 p-2">
                                <TrendingUp className="h-5 w-5 text-accent" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Contribuables visités (30js)</p>
                                <p className="text-xl font-bold text-foreground">
                                    {last_30_days?.visited_tax_payers_count ?? 0}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <Card className="border-border bg-card p-4">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg bg-warning/10 p-2">
                                <Users className="h-5 w-5 text-warning" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Contribuables assignés</p>
                                <p className="text-xl font-bold text-foreground">
                                    {last_30_days?.assigned_tax_payers_count ?? 0}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>


                        <div className="grid gap-6 lg:grid-cols-2">
                            <Card className="border-border bg-card p-6">
                                <h3 className="mb-4 text-lg font-semibold text-foreground">Informations de l'utilisateur</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <Building className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Commune</p>
                                            <p className="font-mono text-sm text-foreground">{user.first_community_name}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Phone className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Téléphone</p>
                                            <p className="font-mono text-sm text-foreground">{user.contact}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Email</p>
                                            <p className="text-sm text-foreground">{user.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Role</p>
                                            <p className="text-sm font-medium text-foreground">{user.role}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Join Date</p>
                                            <p className="text-sm text-foreground">{new Date(user.created_at!).toLocaleString()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Activity className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Device assigné</p>
                                            <p className="font-mono text-sm text-foreground">
                                            {primaryDevice ? primaryDevice.serial_number : "-"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Activity className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                            <p className="text-xs text-muted-foreground">Dernière synchro</p>
                                            <p className="text-sm text-success">
                                                {primaryDevice?.last_sync_at ? new Date(primaryDevice.last_sync_at).toLocaleString() : "-"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            <Card className="border-border bg-card p-6">
                                <h3 className="mb-4 text-lg font-semibold text-foreground">Dernières collectes</h3>
                                {latest_collections.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Aucune collecte récente.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {latest_collections.map((collection) => (
                                            <div key={collection.id} className="rounded-lg border border-border p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <p className="font-semibold text-foreground">
                                                                {collection.tax_payer_name || "Contribuable"}
                                                            </p>
                                                            <span className="rounded-full bg-success/10 px-2 py-0.5 font-mono text-xs text-success">
                                                            {Number(collection.amount || 0).toFixed(2)} FCFA
                                                        </span>
                                                        </div>
                                                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                                            <span>{collection.collected_at ? new Date(collection.collected_at).toLocaleString() : "-"}</span>
                                                            <span>Session : {collection.collect_session_code || "-"}</span>
                                                            <span>Terminal : {collection.device_name || "-"}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>


            </div>
        </>
    )
}
